import React, { useState } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Box, 
  Typography, 
  IconButton,
  CircularProgress,
  Paper
} from '@mui/material';
import { 
  Close as CloseIcon, 
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';

const DocumentViewer = ({ document, open, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Handle loading complete
  const handleLoadComplete = () => {
    setLoading(false);
  };

  // Handle loading error
  const handleLoadError = () => {
    setLoading(false);
    setError('Failed to load document preview');
  };

  // Download the document
  const handleDownload = () => {
    window.open(document.download_url, '_blank');
  };

  // Open in new tab
  const handleOpenInNewTab = () => {
    window.open(document.preview_url, '_blank');
  };

  // Render document preview based on mime type
  const renderDocumentPreview = () => {
    if (!document.preview_url) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" gutterBottom>
            Preview not available for this file type.
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            sx={{ mt: 2 }}
          >
            Download File
          </Button>
        </Box>
      );
    }

    if (document.mime_type.startsWith('image/')) {
      return (
        <Box sx={{ position: 'relative', textAlign: 'center' }}>
          {loading && (
            <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
              <CircularProgress />
            </Box>
          )}
          <img 
            src={document.preview_url} 
            alt={document.original_filename}
            style={{ maxWidth: '100%', maxHeight: '70vh', display: loading ? 'none' : 'block' }}
            onLoad={handleLoadComplete}
            onError={handleLoadError}
          />
        </Box>
      );
    }

    if (document.mime_type === 'application/pdf') {
      return (
        <Box sx={{ position: 'relative', height: '70vh' }}>
          {loading && (
            <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
              <CircularProgress />
            </Box>
          )}
          <iframe
            src={document.preview_url}
            title={document.original_filename}
            width="100%"
            height="100%"
            style={{ border: 'none' }}
            onLoad={handleLoadComplete}
            onError={handleLoadError}
          />
        </Box>
      );
    }

    if (document.mime_type === 'text/plain') {
      return (
        <Box sx={{ position: 'relative' }}>
          {loading && (
            <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
              <CircularProgress />
            </Box>
          )}
          <iframe
            src={document.preview_url}
            title={document.original_filename}
            width="100%"
            height="500px"
            style={{ border: 'none' }}
            onLoad={handleLoadComplete}
            onError={handleLoadError}
          />
        </Box>
      );
    }

    // Default case
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body1" gutterBottom>
          Preview not available for this file type.
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          sx={{ mt: 2 }}
        >
          Download File
        </Button>
      </Box>
    );
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, pr: 2 }}>
            {document.original_filename}
          </Typography>
          <Box>
            <IconButton onClick={handleDownload} title="Download">
              <DownloadIcon />
            </IconButton>
            {document.preview_url && (
              <IconButton onClick={handleOpenInNewTab} title="Open in new tab">
                <OpenInNewIcon />
              </IconButton>
            )}
            <IconButton onClick={onClose} title="Close">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {error ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="error">{error}</Typography>
            <Button 
              variant="contained" 
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              sx={{ mt: 2 }}
            >
              Download File Instead
            </Button>
          </Paper>
        ) : (
          renderDocumentPreview()
        )}
      </DialogContent>
      <DialogActions>
        <Typography variant="caption" sx={{ flexGrow: 1, pl: 2 }}>
          File type: {document.mime_type} | Size: {(document.file_size / 1024).toFixed(2)} KB
        </Typography>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentViewer;
